// The appearance control: a three-button segmented group, System / Light /
// Dark, each a pressed-state toggle. It changes the PREFERENCE; what the page
// paints is the resolved theme `ThemeProvider` writes to `<html>`.
//
// Mount it only inside `.loom-focus` or `.loom-dashboard`: its stylesheet reads
// those shells' `--surface`, `--surface-2`, `--border`, `--ink`, `--ink-muted`
// and `--shadow-sm` (plus the root `--focus-ring`), which is what lets it sit
// in either chrome in the chrome's own neutrals.

import { useT, type MessageKey } from "../i18n/index.js";

import { useTheme } from "./ThemeProvider.js";
import type { ThemePreference } from "./theme.js";

import "./theme-toggle.css";

const CHOICES: ReadonlyArray<{
  readonly pref: ThemePreference;
  readonly label: MessageKey;
}> = [
  { pref: "system", label: "theme.system" },
  { pref: "light", label: "theme.light" },
  { pref: "dark", label: "theme.dark" },
];

const svgProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
  focusable: false,
};

const ChoiceIcon = ({ pref }: { readonly pref: ThemePreference }): JSX.Element => {
  switch (pref) {
    case "system":
      return (
        <svg {...svgProps}>
          <rect x="3" y="4" width="18" height="12" rx="2" />
          <path d="M8 20h8M12 16v4" />
        </svg>
      );
    case "light":
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      );
    case "dark":
      return (
        <svg {...svgProps}>
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      );
  }
};

export function ThemeToggle({
  className,
}: {
  readonly className?: string;
}): JSX.Element {
  const t = useT();
  const { preference, setPreference } = useTheme();
  return (
    <div
      role="group"
      aria-label={t("theme.label")}
      className={className ? `theme-toggle ${className}` : "theme-toggle"}
    >
      {CHOICES.map(({ pref, label }) => (
        <button
          key={pref}
          type="button"
          className="theme-toggle-btn"
          aria-pressed={preference === pref}
          aria-label={t(label)}
          title={t(label)}
          data-theme-choice={pref}
          onClick={() => setPreference(pref)}
        >
          <ChoiceIcon pref={pref} />
        </button>
      ))}
    </div>
  );
}
